<template>
  <div class="h5-order-container">
    <!-- 顶部导航栏 -->
    <div class="h5-nav-bar">
      <h1 class="nav-title">我的订单</h1>
      <el-button type="primary" size="small" class="add-btn" @click="openAddDialog">
        <el-icon><Plus /></el-icon> 发布订单
      </el-button>
    </div>

    <!-- 筛选区域（默认折叠，点击展开） -->
    <div class="filter-wrapper">
      <el-button
        type="text"
        class="filter-toggle-btn"
        @click="isFilterShow = !isFilterShow"
      >
        {{ isFilterShow ? '收起筛选 ▲' : '展开筛选 ▼' }}
      </el-button>

      <div class="filter-content" v-if="isFilterShow">
        <el-form :model="filterForm" label-width="60px" size="small">
          <!-- 工种筛选 -->
          <el-form-item label="工种">
            <el-select
              v-model="filterForm.jobTypeId"
              placeholder="选择工种"
              clearable
              class="w-100"
            >
              <el-option
                v-for="item in jobTypeList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </el-form-item>



          <!-- 筛选按钮组 -->
          <div class="filter-btn-group">
            <el-button type="primary" size="small" @click="loadOrderList">确认筛选</el-button>
            <el-button type="text" size="small" @click="resetFilter">重置</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 订单列表 -->
    <div class="order-list">
      <el-empty
        v-if="orderList.length === 0 && !isLoading"
        description="暂无订单"
        image-size="100"
      >
        <el-button type="primary" size="small" @click="openAddDialog">发布首个订单</el-button>
      </el-empty>

      <el-skeleton
        v-if="isLoading"
        :count="5"
        type="text"
        :rows="3"
        class="skeleton-item"
      ></el-skeleton>

      <div
        class="order-item"
        v-for="order in orderList"
        :key="order.id"
        @click="openDetailDialog(order)"
      >
        <div class="order-header">
          <h3 class="order-title">{{ order.title }}</h3>
          <span class="order-salary">¥{{ formatIncome(order.income) }}/月</span>
        </div>
        <div class="order-info">
          <div class="info-item">
            <el-icon class="info-icon"><Location /></el-icon>
            <span class="info-text">{{ order.address_detail || '无' }}</span>
          </div>
          <div class="info-item">
            <el-icon class="info-icon"><User /></el-icon>
            <span class="info-text">{{ order.job_type_name || '暂无类型' }}</span>
          </div>
          <div class="info-item">
            <el-icon class="info-icon"><Clock /></el-icon>
            <span class="info-text">{{ formatTime(order.created_at) }}</span>
          </div>
        </div>
        <div class="order-operate">
          <el-button type="text" size="mini" @click.stop="openEditDialog(order)">编辑</el-button>
          <el-button type="text" size="mini" text-color="#ff4d4f" @click.stop="handleDelete(order)">删除</el-button>
        </div>
      </div>
    </div>

    <!-- 分页组件（手机端简化） -->
    <div class="pagination-wrapper" v-if="orderList.length > 0 && !isLoading">
      <el-pagination
        v-model:current-page="pageInfo.currentPage"
        v-model:page-size="pageInfo.pageSize"
        :page-sizes="[5, 10, 15]"
        :total="pageInfo.total"
        layout="prev, pager, next, ->, sizes"
        @size-change="loadOrderList"
        @current-change="loadOrderList"
        small
      ></el-pagination>
    </div>

    <!-- 新增/编辑订单弹窗（复用百度地图地址搜索逻辑） -->
    <el-dialog
      v-model="formDialogVisible"
      :title="isEdit ? '编辑订单' : '发布订单'"
      :width="isMobile ? '90%' : '600px'"
      append-to-body
      @close="handleDialogClose"
      :close-on-click-modal="false"
    >
      <el-form
        :model="form"
        ref="formRef"
        label-width="70px"
        :rules="formRules"
        class="form-container"
        label-position="left"
        size="small"
      >
        <!-- 基础信息 -->
        <el-row :gutter="15" class="mb-3">
          <el-col :span="24">
            <el-form-item label="订单标题" prop="title">
              <el-input
                v-model="form.title"
                placeholder="请输入订单标题（如：朝阳月嫂兼职）"
                clearable
                maxlength="50"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="工种类型" prop="job_type">
              <el-select
                v-model="form.job_type"
                placeholder="请选择工种"
                clearable
                class="w-100"
              >
                <el-option
                  v-for="item in jobTypeList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="薪资收入" prop="income">
              <el-input
                v-model.number="form.income"
                placeholder="请输入薪资（元）"
                type="number"
                clearable
                step="0.01"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="contact_phone">
              <el-input
                v-model="form.contact_phone"
                placeholder="请输入11位手机号"
                clearable
                maxlength="11"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 详细地址（百度地址搜索+自动填充） -->
        <el-col :span="24">
          <el-form-item label="详细地址" prop="address_detail">
            <div class="address-search-wrapper relative" style="z-index: 9999;">
              <el-input
                v-model="form.address_detail"
                placeholder="输入关键词（例如小区名）自动定位"
                @input="debounceSearch"
                @focus="showDropdown = true"
                @blur="handleInputBlur"
                clearable
                class="w-100"
              ></el-input>

              <!-- 地址下拉框（适配移动端） -->
              <div
                v-if="showDropdown"
                class="address-dropdown"
                style="
                  position: absolute;
                  top: 100%;
                  left: 0;
                  right: 0;
                  max-height: 250px;
                  overflow-y: auto;
                  background: #fff;
                  border: 1px solid #e6e6e6;
                  border-radius: 4px;
                  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
                  z-index: 10000;
                  margin-top: 2px;
                  padding: 4px 0;
                "
              >
                <div v-if="isSearching" style="padding: 12px; text-align: center; color: #999; font-size: 12px;">
                  <el-icon style="margin-right: 6px;"><Loading /></el-icon>
                  搜索中...
                </div>
                <div v-else-if="addressList.length > 0">
                  <div
                    v-for="(addr, idx) in addressList"
                    :key="idx"
                    class="dropdown-item"
                    :class="{ active: activeIndex === idx }"
                    @mouseenter="activeIndex = idx"
                    @click="selectAddress(addr)"
                    style="padding: 10px 12px; cursor: pointer; transition: background 0.2s; font-size: 13px;"
                  >
                    <div style="font-weight: 500; color: #333; margin-bottom: 3px;">{{ addr.name }}</div>
                    <div style="font-size: 11px; color: #666; margin-bottom: 2px;">
                      {{ addr.province }} {{ addr.city }} {{ addr.district }} {{ addr.address }}
                    </div>
                    <div style="font-size: 10px; color: #999;">
                      <span v-if="addr.location">
                        经纬度：{{ addr.location.lat.toFixed(6) }}, {{ addr.location.lng.toFixed(6) }}
                      </span>
                      <span v-else>经纬度：无</span>
                    </div>
                  </div>
                </div>
                <div v-else style="padding: 12px; text-align: center; color: #999; font-size: 12px;">
                  未找到匹配地址，请更换关键词
                </div>
              </div>
            </div>
          </el-form-item>
        </el-col>

        <!-- 订单描述 -->
        <el-form-item label="订单描述" prop="description">
          <el-input
            v-model="form.description"
            placeholder="请详细描述工作内容、要求等（可选）"
            type="textarea"
            :rows="3"
          ></el-input>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button size="small" @click="formDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="submitForm">确认提交</el-button>
      </template>
    </el-dialog>

    <!-- 订单详情弹窗 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="订单详情"
      :width="isMobile ? '90%' : '600px'"
      append-to-body
    >
      <div class="detail-content">
        <div class="detail-item">
          <span class="detail-label">订单标题：</span>
          <span class="detail-value">{{ detailForm.title || '无' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">所属工种：</span>
          <span class="detail-value">{{ detailForm.job_type_name || '无' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">薪资待遇：</span>
          <span class="detail-value">¥{{ formatIncome(detailForm.income) }}/月</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">联系电话：</span>
          <span class="detail-value">{{ detailForm.contact_phone || '无' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">工作地址：</span>
          <span class="detail-value">{{ detailForm.full_address || detailForm.address_detail || '无' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">发布时间：</span>
          <span class="detail-value">{{ formatTime(detailForm.created_at) }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">订单描述：</span>
          <span class="detail-value">{{ detailForm.description || '无' }}</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue';
import { ElMessage, ElEmpty, ElSkeleton, ElPagination, ElDialog, ElForm, ElFormItem, ElInput, ElSelect, ElOption, ElButton, ElIcon, ElMessageBox } from 'element-plus';
import { Plus, Location, User, Clock, Loading } from '@element-plus/icons-vue';
import http from '@/util/request';
import dayjs from 'dayjs';

// ========== 设备判断（适配手机端） ==========
const isMobile = computed(() => {
  return window.innerWidth < 768;
});

// ========== 状态管理 ==========
// 筛选表单
const filterForm = reactive({
  jobTypeId: undefined,
  addressKeyword: '',
  minIncome: undefined,
  maxIncome: undefined
});

// 分页信息
const pageInfo = reactive({
  currentPage: 1,
  pageSize: isMobile.value ? 5 : 10,
  total: 0
});

// 订单相关状态
const orderList = ref([]);
const jobTypeList = ref([]);
const isLoading = ref(false);
const isFilterShow = ref(false);

// 弹窗相关状态
const formDialogVisible = ref(false);
const detailDialogVisible = ref(false);
const isEdit = ref(false);
const formRef = ref(null);
const detailForm = ref({});

// ========== 地址搜索相关（复用你的逻辑） ==========
const form = ref({
  title: '',
  job_type: '',
  income: '',
  contact_phone: '',
  province: '',
  city: '',
  district: '',
  address_detail: '',
  latitude: '',
  longitude: '',
  description: '',
  publisher: window.sessionStorage.getItem('userId') || '' // 发布者ID（登录后存储）
});

const showDropdown = ref(false);
const activeIndex = ref(-1);
const addressList = ref([]);
const isSearching = ref(false);
const searchTimer = ref(null);

// ========== 表单验证规则（和你的一致） ==========
const formRules = reactive({
  title: [
    { required: true, message: '请输入订单标题', trigger: 'blur' },
    { min: 2, max: 50, message: '标题长度2-50个字符', trigger: 'blur' }
  ],
  job_type: [
    { required: true, message: '请选择工种类型', trigger: 'change' }
  ],
  income: [
    { required: true, message: '请输入薪资', trigger: 'blur' },
    { type: 'number', min: 0.01, message: '薪资必须大于0', trigger: 'blur' }
  ],
  contact_phone: [
    { required: true, message: '请输入联系电话', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入11位有效手机号', trigger: 'blur' }
  ],
  address_detail: [
    { required: true, message: '请选择详细地址', trigger: 'blur' }
  ],
  province: [
    { required: true, message: '请选择地址（自动填充省份）', trigger: 'blur' }
  ],
  city: [
    { required: true, message: '请选择地址（自动填充城市）', trigger: 'blur' }
  ]
});

// ========== 核心方法 ==========
/**
 * 加载工种列表
 */
const loadJobTypeList = async () => {
  try {
    const res = await http.get('/api/ayi/job-types/');
    jobTypeList.value = Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    ElMessage.error('获取工种失败：' + (error.message || '网络错误'));
  }
};

/**
 * 加载订单列表（员工端：只查自己的订单）
 */
const loadOrderList = async () => {
  isLoading.value = true;
  try {
    const params = {
      page: pageInfo.currentPage,
      page_size: pageInfo.pageSize,
      job_type: filterForm.jobTypeId,
      address_keyword: filterForm.addressKeyword.trim(),
      min_income: filterForm.minIncome,
      max_income: filterForm.maxIncome
    };

    // 移除无效参数
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    });

    const res = await http.get('/api/dingdan/my-orders/', {
      headers: {
        'X-User-Info': encodeURIComponent(JSON.stringify({
          id: window.sessionStorage.getItem('userId') || ''
        }))
      },
      params
    });

    if (res.data?.results) {
      // 强制转换income为数字
      orderList.value = res.data.results.map(item => ({
        ...item,
        income: parseFloat(item.income) || 0
      }));
      pageInfo.total = res.data.count;
    } else {
      orderList.value = Array.isArray(res.data) ? res.data.map(item => ({
        ...item,
        income: parseFloat(item.income) || 0
      })) : [];
      pageInfo.total = orderList.value.length;
    }
  } catch (error) {
    orderList.value = [];
    pageInfo.total = 0;
    ElMessage.error('获取订单失败：' + (error.message || '网络错误'));
  } finally {
    isLoading.value = false;
  }
};

/**
 * 重置筛选
 */
const resetFilter = () => {
  filterForm.jobTypeId = undefined;
  filterForm.addressKeyword = '';
  filterForm.minIncome = undefined;
  filterForm.maxIncome = undefined;
  loadOrderList();
};

/**
 * 打开新增订单弹窗
 */
const openAddDialog = () => {
  isEdit.value = false;
  // 重置表单
  form.value = {
    title: '',
    job_type: '',
    income: '',
    contact_phone: '',
    province: '',
    city: '',
    district: '',
    address_detail: '',
    latitude: '',
    longitude: '',
    description: '',
    publisher: window.sessionStorage.getItem('userId') || ''
  };
  nextTick(() => {
    formRef.value?.resetFields();
  });
  formDialogVisible.value = true;
};

/**
 * 打开编辑订单弹窗
 */
const openEditDialog = (row) => {
  isEdit.value = true;
  // 回显数据（包含省市区、经纬度）
  form.value = {
    id: row.id,
    title: row.title || '',
    job_type: row.job_type,
    income: row.income || 0,
    contact_phone: row.contact_phone || '',
    province: row.province || '',
    city: row.city || '',
    district: row.district || '',
    address_detail: row.address_detail || '',
    latitude: row.latitude || '',
    longitude: row.longitude || '',
    description: row.description || '',
    publisher: window.sessionStorage.getItem('userId') || ''
  };
  nextTick(() => {
    formRef.value?.resetFields();
  });
  formDialogVisible.value = true;
};

/**
 * 打开订单详情弹窗
 */
const openDetailDialog = (row) => {
  detailForm.value = { ...row };
  detailDialogVisible.value = true;
};

/**
 * 地址搜索防抖
 */
const debounceSearch = () => {
  clearTimeout(searchTimer.value);
  searchTimer.value = setTimeout(async () => {
    const keyword = form.value.address_detail.trim();
    if (!keyword) {
      addressList.value = [];
      return;
    }
    await searchAddress(keyword);
  }, 300);
};

/**
 * 调用百度地图接口搜索地址
 */
const searchAddress = async (keyword) => {
  isSearching.value = true;
  try {
        const res = await http.get('/api/key/proxy/baidu-map/', { keyword });
    const data = res.data || {};
    if (data.status === 0) {
      // 过滤无经纬度的地址
      addressList.value = (data.result || []).filter(addr => {
        return addr.location &&
               typeof addr.location.lat === 'number' &&
               typeof addr.location.lng === 'number';
      });
    } else {
      addressList.value = [];
      ElMessage.warning(data.message || '地址搜索失败');
    }
  } catch (error) {
    addressList.value = [];
    ElMessage.error('地址搜索失败：' + (error.message || '网络异常'));
  } finally {
    isSearching.value = false;
  }
};

/**
 * 选择地址：自动填充省市区、经纬度、详细地址
 */
const selectAddress = (addr) => {
  // 校验经纬度有效性
  if (!addr.location || typeof addr.location.lat !== 'number' || typeof addr.location.lng !== 'number') {
    ElMessage.warning('该地址暂无精确经纬度，请选择其他地址');
    return;
  }

  form.value.province = addr.province;
  form.value.city = addr.city;
  form.value.district = addr.district || '';
  form.value.address_detail = `${addr.province} ${addr.city} ${addr.district || ''} ${addr.address}`.trim();
  form.value.latitude = addr.location.lat;
  form.value.longitude = addr.location.lng;
  showDropdown.value = false;
  activeIndex.value = -1;
};

/**
 * 输入框失焦关闭下拉框
 */
const handleInputBlur = () => {
  setTimeout(() => showDropdown.value = false, 200);
};

/**
 * 弹窗关闭回调
 */
const handleDialogClose = () => {
  showDropdown.value = false;
  clearTimeout(searchTimer.value);
};

/**
 * 提交订单（新增/编辑）
 */
const submitForm = async () => {
  try {
    await formRef.value.validate();

    // 构造提交数据（和你的逻辑一致）
    const submitData = {
      ...form.value,
      income: Number(form.value.income),
      latitude: form.value.latitude ? Number(form.value.latitude) : null,
      longitude: form.value.longitude ? Number(form.value.longitude) : null,
      job_type: Number(form.value.job_type)
    };

    // 员工端接口：/my-orders/
    if (isEdit.value) {
      await http.put(`/api/dingdan/my-orders/${form.value.id}/`, submitData, {
        headers: {
          'X-User-Info': encodeURIComponent(JSON.stringify({
            id: window.sessionStorage.getItem('userId') || ''
          }))
        }
      });
      ElMessage.success('订单编辑成功！');
    } else {
      await http.post('/api/dingdan/my-orders/', submitData, {
        headers: {
          'X-User-Info': encodeURIComponent(JSON.stringify({
            id: window.sessionStorage.getItem('userId') || ''
          }))
        }
      });
      ElMessage.success('订单发布成功！');
    }

    formDialogVisible.value = false;
    loadOrderList(); // 刷新订单列表

    // 重置表单
    nextTick(() => {
      formRef.value?.resetFields();
      form.value = {
        title: '',
        job_type: '',
        income: '',
        contact_phone: '',
        province: '',
        city: '',
        district: '',
        address_detail: '',
        latitude: '',
        longitude: '',
        description: '',
        publisher: window.sessionStorage.getItem('userId') || ''
      };
    });
  } catch (error) {
    const errorMsg = error.response?.data?.msg || error.response?.data || error.message;
    ElMessage.error('提交失败：' + JSON.stringify(errorMsg));
    console.error('提交错误详情：', error);
  }
};

/**
 * 删除订单
 */
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除该订单吗？删除后不可恢复',
      '提示',
      {
        type: 'warning',
        size: 'small'
      }
    );

    await http.del(`/api/dingdan/my-orders/${row.id}/`, {
      headers: {
        'X-User-Info': encodeURIComponent(JSON.stringify({
          id: window.sessionStorage.getItem('userId') || ''
        }))
      }
    });

    ElMessage.success('订单删除成功');
    loadOrderList();
  } catch (error) {
    if (error.name !== 'Cancel') {
      ElMessage.error('删除订单失败：' + (error.message || '网络错误'));
    }
  }
};

// ========== 工具函数 ==========
/**
 * 安全格式化薪资
 */
const formatIncome = (val) => {
  const income = parseFloat(val) || 0;
  return income.toFixed(2);
};

/**
 * 格式化时间
 */
const formatTime = (val) => {
  if (!val) return '无';
  return dayjs(val).format('YYYY-MM-DD HH:mm');
};

// ========== 初始化加载 ==========
onMounted(() => {
  loadJobTypeList();
  loadOrderList();

  // 监听窗口大小变化，调整分页大小
  window.addEventListener('resize', () => {
    pageInfo.pageSize = isMobile.value ? 5 : 10;
  });
});
</script>

<style scoped>
/* 基础容器样式 */
.h5-order-container {
  padding: 10px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

/* 顶部导航栏 */
.h5-nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.nav-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.add-btn {
  padding: 6px 12px;
}

/* 筛选区域 */
.filter-wrapper {
  background-color: #fff;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 15px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.filter-toggle-btn {
  color: #409eff;
  font-size: 14px;
  width: 100%;
  text-align: left;
}

.filter-content {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #eee;
}

.el-form-item {
  margin-bottom: 12px;
}

.salary-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.salary-input {
  flex: 1;
}

.salary-separator {
  color: #666;
}

.filter-btn-group {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

/* 订单列表 */
.order-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.order-item {
  background-color: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: background-color 0.2s;
}

.order-item:hover {
  background-color: #fafafa;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.order-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.order-salary {
  font-size: 15px;
  font-weight: 600;
  color: #ff4d4f;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;
  gap: 6px;
}

.info-icon {
  font-size: 14px;
  color: #888;
}

.info-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.order-operate {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 分页组件 */
.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  padding-bottom: 20px;
}

/* 弹窗表单：宽度、输入框优化 */
.el-dialog {
  width: 95% !important; /* 横屏几乎铺满，最大宽度限制 */
  max-width: 600px;
}
.el-dialog__body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 15px;
}
.form-container {
  padding: 10px 0;
}

/* 详细地址输入框：高度+横屏铺满 */
.address-search-wrapper {
  width: 100%;
}
.address-search-wrapper .el-input {
  width: 100% !important;
  min-height: 48px; /* 调整输入框高度，可根据需要修改 */
}
.el-form-item__content {
  padding: 0 !important; /* 去除表单项内边距，确保输入框铺满 */
}

.dropdown-item.active {
  background-color: #f0f7ff;
  color: #1890ff;
}

/* 详情弹窗 */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
}

.detail-item {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.detail-label {
  color: #666;
  min-width: 70px;
}

.detail-value {
  color: #333;
  flex: 1;
}

/* 响应式适配 */
@media (max-width: 375px) {
  .order-title {
    font-size: 15px;
  }
  .order-salary {
    font-size: 14px;
  }
  .info-item {
    font-size: 12px;
  }
  .el-form-item {
    margin-bottom: 10px;
  }
  .el-form-item__label {
    font-size: 12px;
  }
  .el-form-item__content {
    padding: 0 !important;
  }
}

/* 骨架屏样式 */
.skeleton-item {
  margin-bottom: 10px;
  border-radius: 8px;
}
</style>
